import get from "lodash.get"
import dayjs from "dayjs"

import {
	getLevyRatio,
	getLevyFlat,
	getContacts,
	getIdentities,
	getLoadings,
	getDiscounts,
	getAddresses,
	getFacts,
	getRejectionCodesArray,
	getRejectionRemarks,
	getExclusions,
	getTransactions,
	getPriceBreakdown
} from "@/modules/reports/schemas/utils/"

const createSchema = () => {
	const users = [
		{
			label: "Other Holder",
			key: "otherHolders",
			max: 10
		},
		{
			label: "Insured",
			key: "insured",
			max: 10
		}
	]

	let output = [
		{ label: "Quote Number", data: item => item.id },
		{ label: "Client", data: item => get(item, "holder.name") },
		{ label: "Client Type", data: item => get(item, "holder.__typename") },
		{
			label: "Start Date",
			data: item =>
				item.startDate ? dayjs(item.startDate).format("DD/MM/YYYY") : null
		},
		{
			label: "End Date",
			data: item =>
				item.endDate ? dayjs(item.endDate).format("DD/MM/YYYY") : null
		},
		{ label: "Status", data: item => item.status },
		{ label: "Rejection Code(s)", data: item => getRejectionCodesArray(item) },
		{ label: "Rejection Remark", data: item => getRejectionRemarks(item) },
		{
			label: "Created At",
			data: item =>
				item.createdAt ? dayjs(item.createdAt).format("DD/MM/YYYY") : null
		},

		{ label: "Source", data: item => item.source },

		{ label: "Channel", data: item => get(item, "client.name") },

		{
			label: "Product Name",
			data: item => get(item, "acceptedOffer.product.name")
		},
		{
			label: "Product Type",
			data: item => get(item, "acceptedOffer.product.productId.type")
		},
		{
			label: "Product Version",
			data: item => get(item, "acceptedOffer.product.productId.version")
		},
		{
			label: "Insurer",
			data: item => get(item, "acceptedOffer.product.insurer.name")
		},

		{
			label: "Gross Premium",
			data: item => get(item, "acceptedOffer.premium.formattedOriginalPrice")
		},

		{
			label: "Loadings",
			data: item => getLoadings(item)
		},

		{
			label: "Discounts",
			data: item => getDiscounts(item)
		},

		{
			label: "Levy %",
			data: item => getLevyRatio(item) * 100
		},
		{
			label: "Levy Amount",
			data: item => getLevyFlat(item)
		},

		{
			label: "Net Premium",
			data: item => get(item, "acceptedOffer.premium.formattedPrice")
		},

		{
			label: "Price Breakdown",
			data: item => getPriceBreakdown(get(item, "acceptedOffer.premium"))
		},

		{
			label: "Exclusions",
			data: item => getExclusions(item)
		},

		{
			label: "Transactions",
			data: item => getTransactions(item)
		},

		{
			label: "Referral Code",
			data: item => get(item, "referrer.internalCode")
		},
		{ label: "Referral Name", data: item => get(item, "referrer.name") },
		{
			label: "Commission Type",
			data: item => get(item, "commissionSummary.type")
		},
		{
			label: "Commission %",
			data: item => get(item, "commissionSummary.ratio", null) * 100
		},
		{
			label: "Commission Amount",
			data: item => get(item, "commissionSummary.amount")
		},

		{
			label: "Holder Type",
			data: item => get(item, "holder.__typename")
		},
		{
			label: "Holder Name",
			data: item => get(item, "holder.name")
		},

		// For Individuals
		{
			label: "Holder Gender",
			data: item => get(item, "holder.gender")
		},
		{
			label: "Holder Salutation",
			data: item => get(item, "holder.salutation")
		},
		{
			label: "Holder English First Name",
			data: item => get(item, "holder.englishFirstName")
		},
		{
			label: "Holder English Last Name",
			data: item => get(item, "holder.englishLastName")
		},
		{
			label: "Holder Chinese First Name",
			data: item => get(item, "holder.chineseFirstName")
		},
		{
			label: "Holder Chinese Last Name",
			data: item => get(item, "holder.chineseLastName")
		},
		{
			label: "Holder Date Of Birth",
			data: item => get(item, "holder.dateOfBirth")
		},
		{
			label: "Holder Marital Status",
			data: item => get(item, "holder.maritalStatus")
		},
		{
			label: "Holder Occupation",
			data: item => get(item, "holder.occupation")
		},
		{
			label: "Holder Country of Residency",
			data: item => get(item, "holder.countryOfResidency")
		},
		{
			label: "Holder Preferred Communication Channel",
			data: item => get(item, "holder.preferredCommunicationChannel")
		},
		{
			label: "Holder Preferred Languages",
			data: item => get(item, "holder.preferredLanguage")
		},

		//For companies
		{
			label: "Holder Nature of Business",
			data: item => get(item, "holder.natureOfBusiness")
		},
		{
			label: "Holder Registration Number",
			data: item => get(item, "holder.registrationNumber")
		},

		//For all types
		{
			label: "Holder Accepts Marketing",
			data: item => get(item, "holder.acceptsMarketing")
		},
		{
			label: "Holder Email",
			data: item =>
				get(item, "holder.contacts", null)
					? item.holder.contacts
							.filter(item => item.type === "email")
							.map(item => item.value)
					: ""
		},
		{
			label: "Holder Contact Number",
			data: item =>
				get(item, "holder.contacts", null)
					? item.holder.contacts
							.filter(item => item.type === "telephoneNumber")
							.map(item => item.value)
					: ""
		},

		{
			label: "Holder Identities",
			data: item => getIdentities(get(item, "holder.identities"))
		},
		{
			label: "Holder Addresses",
			data: item => getAddresses(get(item, "holder.addresses"))
		},
		{
			label: "Holder Facts",
			data: item => getFacts(get(item, "holder.facts"))
		}
	]

	// create otherHolders and insured columns
	for (let i = 0; i < users.length; i++) {
		for (let j = 0; j < users[i].max; j++) {
			output.push(
				{
					label: `${users[i].label} ${j + 1} Type`,
					data: item => get(item, `${users[i].key}[${j}].__typename`, "")
				},
				{
					label: `${users[i].label} ${j + 1} Name`,
					data: item => get(item, `${users[i].key}[${j}].name`, "")
				},
				{
					label: `${users[i].label} ${j + 1} Gender`,
					data: item => get(item, `${users[i].key}[${j}].gender`)
				},
				{
					label: `${users[i].label} ${j + 1} Salutation`,
					data: item => get(item, `${users[i].key}[${j}].salutation`)
				},
				{
					label: `${users[i].label} ${j + 1} English First Name`,
					data: item => get(item, `${users[i].key}[${j}].englishFirstName`)
				},
				{
					label: `${users[i].label} ${j + 1} English Last Name`,
					data: item => get(item, `${users[i].key}[${j}].englishLastName`)
				},
				{
					label: `${users[i].label} ${j + 1} Chinese First Name`,
					data: item => get(item, `${users[i].key}[${j}].chineseFirstName`)
				},
				{
					label: `${users[i].label} ${j + 1} Chinese Last Name`,
					data: item => get(item, `${users[i].key}[${j}].chineseLastName`)
				},
				{
					label: `${users[i].label} ${j + 1} Date Of Birth`,
					data: item => get(item, `${users[i].key}[${j}].dateOfBirth`)
				},
				{
					label: `${users[i].label} ${j + 1} Marital Status`,
					data: item => get(item, `${users[i].key}[${j}].maritalStatus`)
				},
				{
					label: `${users[i].label} ${j + 1} Occupation`,
					data: item => get(item, `${users[i].key}[${j}].occupation`)
				},
				{
					label: `${users[i].label} ${j + 1} Country of Residency`,
					data: item => get(item, `${users[i].key}[${j}].countryOfResidency`)
				},
				{
					label: `${users[i].label} ${j + 1} Contacts`,
					data: item => getContacts(get(item, `${users[i].key}[${j}].contacts`))
				},
				{
					label: `${users[i].label} ${j + 1} Identities`,
					data: item =>
						getIdentities(get(item, `${users[i].key}[${j}].identities`))
				},
				{
					label: `${users[i].label} ${j + 1} Addresses`,
					data: item =>
						getAddresses(get(item, `${users[i].key}[${j}].addresses`))
				},
				{
					label: `${users[i].label} ${j + 1} Facts`,
					data: item => getFacts(get(item, `${users[i].key}[${j}].facts`))
				}
			)
		}
	}

	return output
}

export default {
	quotes: [...createSchema()]
}
